import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatBRL } from "../lib/api";
import { Users, Target, Receipt, Trash2, Plus, X, Trophy, Search, AlertTriangle, Filter, Crown, Medal, KeyRound, Shield, ShieldOff, UserPlus, Gift, Zap, Sparkles, RotateCcw, ShoppingBag, Image as ImageIcon, Package, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { fireConfetti } from "../lib/confetti";

const CAT_MAP = {
  aparelho: { label: "📱 Aparelho", chip: "chip-pink" },
  acessorio: { label: "🎧 Acessório", chip: "chip-cyan" },
  plano: { label: "📶 Plano", chip: "chip-green" },
  seguro: { label: "🛡️ Seguro", chip: "chip-gold" },
};

export default function Admin() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("ranking");
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null); // user obj
  const [form, setForm] = useState({
    title: "", description: "", scope: "daily", target_type: "sales_count", target: 1, xp_reward: 100,
  });
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "seller" });
  const [newPw, setNewPw] = useState("");
  const [usersFilter, setUsersFilter] = useState("all"); // all | seller | observer | admin

  // Bonus (admin-granted XP) state
  const [bonuses, setBonuses] = useState([]);
  const [bonusForm, setBonusForm] = useState({ user_id: "", points: 100, reason: "" });
  const BONUS_PRESETS = [
    { label: "Portabilidade", pts: 300, reason: "Portabilidade" },
    { label: "Super venda", pts: 500, reason: "Super venda" },
    { label: "Elogio cliente", pts: 150, reason: "Elogio do cliente" },
    { label: "Treinamento", pts: 100, reason: "Treinamento concluído" },
  ];

  // Store state
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [rewardForm, setRewardForm] = useState({ name: "", description: "", cost: 10, stock: 10, image_url: "" });

  // Sales conference filters
  const [q, setQ] = useState("");
  const [filterSeller, setFilterSeller] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [onlyDupes, setOnlyDupes] = useState(false);

  const loadAll = async () => {
    try {
      const [s, u, m, r, b, rw, rd] = await Promise.all([
        api.get("/sales"),
        api.get("/users"),
        api.get("/missions"),
        api.get("/ranking"),
        api.get("/bonuses"),
        api.get("/rewards?include_inactive=true"),
        api.get("/redemptions"),
      ]);
      setSales(s.data);
      setUsers(u.data);
      setMissions(m.data);
      setRanking(r.data);
      setBonuses(b.data);
      setRewards(rw.data);
      setRedemptions(rd.data);
    } catch {}
  };

  useEffect(() => { loadAll(); }, []);

  const deleteSale = async (id) => {
    if (!window.confirm("Excluir esta venda? O XP e as missões serão recalculados.")) return;
    try {
      const { data } = await api.delete(`/sales/${id}`);
      if (data?.mission_claims_revoked > 0) {
        toast.success(`Venda excluída. ${data.mission_claims_revoked} recompensa(s) de missão revertida(s).`);
      } else {
        toast.success("Venda excluída. XP recalculado.");
      }
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const grantBonus = async (e) => {
    e.preventDefault();
    if (!bonusForm.user_id) {
      toast.error("Selecione um vendedor");
      return;
    }
    if (!bonusForm.reason.trim()) {
      toast.error("Descreva o motivo do bônus");
      return;
    }
    const pts = parseInt(bonusForm.points);
    if (!pts || pts <= 0) {
      toast.error("Pontos deve ser maior que 0");
      return;
    }
    try {
      await api.post("/bonuses", {
        user_id: bonusForm.user_id,
        points: pts,
        reason: bonusForm.reason.trim(),
      });
      fireConfetti();
      const seller = users.find((u) => u.id === bonusForm.user_id);
      toast.success(`+${pts} XP para ${seller?.name || "vendedor"}!`);
      setBonusForm({ user_id: "", points: 100, reason: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteBonus = async (b) => {
    if (!window.confirm(`Remover bônus de ${b.points} XP (${b.reason}) de ${b.user_name}?`)) return;
    try {
      await api.delete(`/bonuses/${b.id}`);
      toast.success("Bônus removido. XP recalculado.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Store handlers
  const handleRewardImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Imagem muito grande (máx 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 400 / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        setRewardForm({ ...rewardForm, image_url: c.toDataURL("image/jpeg", 0.82) });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
  };

  const createReward = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rewards", {
        name: rewardForm.name,
        description: rewardForm.description,
        cost: parseInt(rewardForm.cost),
        stock: parseInt(rewardForm.stock),
        image_url: rewardForm.image_url || null,
      });
      toast.success("Prêmio cadastrado!");
      setRewardForm({ name: "", description: "", cost: 10, stock: 10, image_url: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteReward = async (r) => {
    if (!window.confirm(`Remover "${r.name}" da loja?`)) return;
    try {
      await api.delete(`/rewards/${r.id}`);
      toast.success("Prêmio removido");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markDelivered = async (r) => {
    try {
      await api.patch(`/redemptions/${r.id}/deliver`);
      toast.success("Marcado como entregue");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteMission = async (id) => {
    if (!window.confirm("Excluir missão?")) return;
    try {
      await api.delete(`/missions/${id}`);
      toast.success("Missão excluída");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createMission = async (e) => {
    e.preventDefault();
    try {
      await api.post("/missions", {
        ...form,
        target: parseFloat(form.target),
        xp_reward: parseInt(form.xp_reward),
      });
      toast.success("Missão criada!");
      setShowNew(false);
      setForm({ title: "", description: "", scope: "daily", target_type: "sales_count", target: 1, xp_reward: 100 });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", userForm);
      toast.success(`${userForm.role === "admin" ? "Admin" : "Vendedor"} criado!`);
      setShowNewUser(false);
      setUserForm({ name: "", email: "", password: "", role: "seller" });
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "seller" : "admin";
    const label = newRole === "admin" ? "promover para ADMIN" : "rebaixar para vendedor";
    if (!window.confirm(`Deseja ${label} "${u.name}"?`)) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role: newRole });
      toast.success("Papel atualizado");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setRole = async (u, newRole) => {
    if (u.role === newRole) return;
    const labels = { admin: "Admin 👑", seller: "Vendedor 🎮", observer: "Observador 👁️" };
    if (!window.confirm(`Mudar "${u.name}" para ${labels[newRole]}?`)) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.name} agora é ${labels[newRole]}`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Excluir "${u.name}"? Isso remove todas as vendas e progressos. Ação irreversível.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário excluído");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetUserXp = async (u) => {
    if (!window.confirm(`Zerar XP de "${u.name}"?\n\nIsso apaga TODAS as vendas, missões resgatadas e bônus desse vendedor. Ele volta ao nível 1 e pode começar do zero. Ação irreversível.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-xp`);
      toast.success(
        `XP de ${u.name} zerado. Removidos: ${data.sales_deleted} vendas, ${data.mission_claims_deleted} missões, ${data.bonuses_deleted} bônus.`
      );
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("Senha deve ter 6+ caracteres");
      return;
    }
    try {
      await api.post(`/users/${showResetPw.id}/reset-password`, { password: newPw });
      toast.success("Senha redefinida");
      setShowResetPw(null);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Detect duplicates: same seller + client + value + category within 10 minutes
  const dupeIds = useMemo(() => {
    const groups = {};
    for (const s of sales) {
      const key = `${s.seller_id}|${s.client_name.trim().toLowerCase()}|${s.value}|${s.category || ""}`;
      (groups[key] = groups[key] || []).push(s);
    }
    const ids = new Set();
    Object.values(groups).forEach((arr) => {
      if (arr.length < 2) return;
      // sort by created_at
      arr.sort((a, b) => a.created_at.localeCompare(b.created_at));
      for (let i = 1; i < arr.length; i++) {
        const dt = new Date(arr[i].created_at) - new Date(arr[i - 1].created_at);
        if (dt <= 10 * 60 * 1000) {
          ids.add(arr[i].id);
          ids.add(arr[i - 1].id);
        }
      }
    });
    return ids;
  }, [sales]);

  const filteredSales = useMemo(() => {
    const term = q.trim().toLowerCase();
    return sales.filter((s) => {
      if (onlyDupes && !dupeIds.has(s.id)) return false;
      if (filterSeller && s.seller_id !== filterSeller) return false;
      if (filterCat && s.category !== filterCat) return false;
      if (!term) return true;
      return (
        s.product?.toLowerCase().includes(term) ||
        s.client_name?.toLowerCase().includes(term) ||
        s.seller_name?.toLowerCase().includes(term)
      );
    });
  }, [sales, q, filterSeller, filterCat, onlyDupes, dupeIds]);

  const totalFiltered = filteredSales.reduce((acc, s) => acc + (s.value || 0), 0);

  const tabs = [
    { k: "ranking", label: "Ranking", icon: Trophy, testid: "admin-tab-ranking" },
    { k: "sales", label: "Vendas", icon: Receipt, testid: "admin-tab-vendas" },
    { k: "bonus", label: "Bônus", icon: Gift, testid: "admin-tab-bonus" },
    { k: "store", label: "Loja", icon: ShoppingBag, testid: "admin-tab-store" },
    { k: "missions", label: "Missões", icon: Target, testid: "admin-tab-missoes" },
    { k: "users", label: "Equipe", icon: Users, testid: "admin-tab-vendedores" },
  ];

  return (
    <div className="arcade-grid min-h-screen pb-28">
      <div className="phone-frame phone-frame-full px-4 pt-6">
        <div className="mb-4">
          <p className="chip chip-pink mb-2">👑 Painel admin</p>
          <h1 className="font-arcade text-3xl font-extrabold text-white tracking-tight">
            Administração
          </h1>
        </div>

        <div className="grid grid-cols-6 gap-1 bg-slate-900/60 p-1 rounded-xl border border-slate-800 mb-5">
          {tabs.map((t) => {
            const Ico = t.icon;
            return (
              <button
                key={t.k}
                data-testid={t.testid}
                onClick={() => setTab(t.k)}
                className={`py-2 px-0.5 rounded-lg text-[10px] font-arcade font-semibold flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  tab === t.k ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white" : "text-slate-400"
                }`}
              >
                <Ico size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "ranking" && (
          <div className="space-y-2" data-testid="admin-ranking-list">
            <div className="neon-card-gold p-4 mb-3 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shadow-lg">
                <Trophy size={22} className="text-white" strokeWidth={2.4} />
              </div>
              <div>
                <p className="font-arcade text-base font-bold text-white">Ranking Geral</p>
                <p className="text-[11px] text-slate-300">
                  {ranking.length} vendedores competindo
                </p>
              </div>
            </div>
            {ranking.length === 0 && (
              <p className="text-center text-slate-500 py-8 text-sm">Nenhum vendedor ainda</p>
            )}
            {ranking.map((p, idx) => {
              const Ico = idx === 0 ? Crown : idx < 3 ? Medal : null;
              const glow = idx === 0 ? "avatar-gold" : idx === 1 ? "avatar-silver" : idx === 2 ? "avatar-bronze" : "";
              return (
                <div key={p.id} className="neon-card p-4 flex items-center gap-3" data-testid={`admin-ranking-row-${p.rank}`}>
                  <span className="font-arcade text-lg font-bold text-slate-400 w-8 text-center">
                    #{p.rank}
                  </span>
                  <div className={glow + " rounded-full"}>
                    {Ico ? (
                      <div className={`w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center`}>
                        <Ico size={20} className="text-white" strokeWidth={2.4} />
                      </div>
                    ) : (
                      <Avatar user={p} size={44} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-arcade text-sm font-bold text-white truncate">{p.name}</p>
                    <p className="text-[11px] font-mono text-slate-400">
                      Nível {p.level} • {p.sales_count} vendas • {formatBRL(p.total_value)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-arcade text-base font-bold text-amber-300">{p.xp}</p>
                    <p className="text-[9px] font-mono text-slate-500 uppercase">XP</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "bonus" && (
          <div className="space-y-4" data-testid="admin-bonus-section">
            <div className="neon-card-gold p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Gift size={22} className="text-white" strokeWidth={2.4} />
                </div>
                <div>
                  <p className="chip chip-gold mb-1">⚡ Pontos extras</p>
                  <h2 className="font-arcade text-lg font-bold text-white leading-tight">Lançar bônus</h2>
                </div>
              </div>
              <form onSubmit={grantBonus} className="space-y-3">
                <div>
                  <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 block">Vendedor</label>
                  <select
                    data-testid="bonus-user-select"
                    value={bonusForm.user_id}
                    onChange={(e) => setBonusForm({ ...bonusForm, user_id: e.target.value })}
                    className="arcade-input text-sm"
                    required
                  >
                    <option value="">Selecione...</option>
                    {users.filter((u) => u.role === "seller").map((u) => (
                      <option key={u.id} value={u.id}>{u.name} — {u.xp || 0} XP</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 block">Atalhos</label>
                  <div className="grid grid-cols-2 gap-2">
                    {BONUS_PRESETS.map((p) => (
                      <button
                        type="button"
                        key={p.label}
                        data-testid={`bonus-preset-${p.label.toLowerCase().replace(/\s/g,"-")}`}
                        onClick={() => setBonusForm({ ...bonusForm, points: p.pts, reason: p.reason })}
                        className="py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-xs font-arcade font-semibold text-slate-300 hover:border-amber-500/50 hover:text-amber-300 flex items-center justify-center gap-1.5"
                      >
                        <Sparkles size={12} /> {p.label} +{p.pts}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-[1fr,120px] gap-2">
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 block">Motivo</label>
                    <input
                      data-testid="bonus-reason-input"
                      value={bonusForm.reason}
                      onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                      placeholder="Ex: Portabilidade cliente João"
                      className="arcade-input text-sm"
                      maxLength={200}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 block">Pontos</label>
                    <input
                      data-testid="bonus-points-input"
                      type="number"
                      min="1"
                      max="100000"
                      value={bonusForm.points}
                      onChange={(e) => setBonusForm({ ...bonusForm, points: e.target.value })}
                      className="arcade-input text-sm font-mono"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="bonus-submit-btn"
                  className="btn-gold w-full flex items-center justify-center gap-2"
                >
                  <Zap size={16} /> Lançar +{bonusForm.points || 0} XP
                </button>
              </form>
            </div>

            <div>
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-2">
                Histórico ({bonuses.length})
              </p>
              <div className="space-y-2" data-testid="admin-bonus-list">
                {bonuses.length === 0 && (
                  <p className="text-center text-slate-500 py-8 text-sm">Nenhum bônus lançado ainda</p>
                )}
                {bonuses.map((b) => (
                  <div key={b.id} className="neon-card p-3 flex items-center gap-3" data-testid="admin-bonus-item">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shrink-0">
                      <Gift size={18} className="text-white" strokeWidth={2.4} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-arcade text-sm font-bold text-white truncate">
                        {b.user_name} <span className="text-amber-300">+{b.points} XP</span>
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">{b.reason}</p>
                      <p className="text-[10px] font-mono text-slate-500">
                        {new Date(b.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <button
                      onClick={() => deleteBonus(b)}
                      data-testid={`admin-delete-bonus-${b.id}`}
                      className="w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-300 hover:bg-red-500/30"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "sales" && (
          <div className="space-y-3">
            <div className="neon-card p-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  data-testid="admin-sales-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por produto, cliente ou vendedor..."
                  className="arcade-input pl-9 py-2.5 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  data-testid="admin-filter-seller"
                  value={filterSeller}
                  onChange={(e) => setFilterSeller(e.target.value)}
                  className="arcade-input py-2.5 text-sm"
                >
                  <option value="">Todos vendedores</option>
                  {users.filter((u) => u.role === "seller").map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <select
                  data-testid="admin-filter-category"
                  value={filterCat}
                  onChange={(e) => setFilterCat(e.target.value)}
                  className="arcade-input py-2.5 text-sm"
                >
                  <option value="">Todas categorias</option>
                  <option value="aparelho">Aparelho</option>
                  <option value="acessorio">Acessório</option>
                  <option value="plano">Plano</option>
                  <option value="seguro">Seguro</option>
                </select>
              </div>
              <button
                data-testid="admin-toggle-duplicates"
                onClick={() => setOnlyDupes((v) => !v)}
                className={`w-full py-2.5 rounded-lg text-xs font-arcade font-semibold flex items-center justify-center gap-2 border transition-colors ${
                  onlyDupes
                    ? "bg-red-500/20 border-red-500/50 text-red-200"
                    : "bg-slate-900/60 border-slate-700 text-slate-400"
                }`}
              >
                <AlertTriangle size={14} />
                {onlyDupes ? "Mostrando duplicadas suspeitas" : `Ver duplicadas suspeitas (${dupeIds.size})`}
              </button>
            </div>

            <div className="flex items-center justify-between px-1">
              <p className="text-[11px] font-mono text-slate-400 uppercase tracking-widest">
                <Filter size={11} className="inline mr-1" />
                {filteredSales.length} vendas
              </p>
              <p className="text-xs font-mono font-bold text-amber-300">
                {formatBRL(totalFiltered)}
              </p>
            </div>

            <div className="space-y-2" data-testid="admin-sales-list">
              {filteredSales.length === 0 && (
                <p className="text-center text-slate-500 py-8 text-sm">Nenhuma venda encontrada</p>
              )}
              {filteredSales.map((s) => {
                const isDupe = dupeIds.has(s.id);
                const cat = s.category ? CAT_MAP[s.category] : null;
                return (
                  <div
                    key={s.id}
                    className={`neon-card p-4 flex items-center gap-3 ${
                      isDupe ? "ring-2 ring-red-500/60 bg-red-500/5" : ""
                    }`}
                    data-testid={isDupe ? "admin-sale-dupe" : "admin-sale-item"}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-arcade text-sm font-bold text-white">{s.product}</p>
                        {cat && <span className={`chip ${cat.chip}`}>{cat.label}</span>}
                        {isDupe && (
                          <span className="chip" style={{ background: "rgba(239,68,68,0.2)", color: "#fca5a5", borderColor: "rgba(239,68,68,0.4)" }}>
                            <AlertTriangle size={10} /> Duplicada?
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 truncate">
                        <span className="font-semibold text-slate-300">{s.seller_name}</span> → {s.client_name}
                      </p>
                      <p className="text-[11px] font-mono text-slate-500">
                        {new Date(s.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="font-arcade text-sm font-bold text-amber-300">{formatBRL(s.value)}</p>
                      <p className="text-[10px] font-mono text-cyan-300">+{s.xp_earned} XP</p>
                    </div>
                    <button
                      onClick={() => deleteSale(s.id)}
                      data-testid="admin-delete-sale-btn"
                      className="w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-300 hover:bg-red-500/30 shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "store" && (
          <div className="space-y-4" data-testid="admin-store-section">
            <div className="neon-card-gold p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shadow-lg">
                  <ShoppingBag size={22} className="text-white" strokeWidth={2.4} />
                </div>
                <div>
                  <p className="chip chip-gold mb-1">🛍️ Loja</p>
                  <h2 className="font-arcade text-lg font-bold text-white leading-tight">Cadastrar prêmio</h2>
                </div>
              </div>
              <form onSubmit={createReward} className="space-y-3">
                <input
                  data-testid="reward-name-input"
                  value={rewardForm.name}
                  onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                  placeholder="Nome do prêmio"
                  className="arcade-input text-sm"
                  required
                />
                <input
                  data-testid="reward-desc-input"
                  value={rewardForm.description}
                  onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })}
                  placeholder="Descrição curta (opcional)"
                  className="arcade-input text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="reward-cost-input"
                    type="number"
                    min="1"
                    value={rewardForm.cost}
                    onChange={(e) => setRewardForm({ ...rewardForm, cost: e.target.value })}
                    placeholder="Custo (🪙)"
                    className="arcade-input text-sm font-mono"
                    required
                  />
                  <input
                    data-testid="reward-stock-input"
                    type="number"
                    min="0"
                    value={rewardForm.stock}
                    onChange={(e) => setRewardForm({ ...rewardForm, stock: e.target.value })}
                    placeholder="Estoque"
                    className="arcade-input text-sm font-mono"
                    required
                  />
                </div>
                <div className="flex items-center gap-3">
                  {rewardForm.image_url && (
                    <img src={rewardForm.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  )}
                  <label className="flex-1 py-3 rounded-xl bg-slate-900/60 border border-slate-700 text-slate-300 font-arcade text-xs font-semibold flex items-center justify-center gap-2 cursor-pointer hover:border-fuchsia-500/50">
                    <ImageIcon size={14} /> {rewardForm.image_url ? "Trocar imagem" : "Adicionar imagem"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleRewardImage} data-testid="reward-image-input" />
                  </label>
                </div>
                <button type="submit" data-testid="reward-submit-btn" className="btn-gold w-full flex items-center justify-center gap-2">
                  <Plus size={16} /> Adicionar à loja
                </button>
              </form>
            </div>

            <div>
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-2">
                Prêmios ({rewards.length})
              </p>
              <div className="grid grid-cols-2 gap-2" data-testid="admin-rewards-list">
                {rewards.length === 0 && (
                  <p className="col-span-2 text-center text-slate-500 py-6 text-sm">Nenhum prêmio ainda</p>
                )}
                {rewards.map((r) => (
                  <div key={r.id} className="neon-card overflow-hidden" data-testid={`admin-reward-${r.id}`}>
                    <div className="aspect-square bg-slate-800">
                      {r.image_url ? (
                        <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Gift size={36} className="text-fuchsia-400/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="font-arcade text-xs font-bold text-white line-clamp-1">{r.name}</p>
                      <p className="font-arcade text-base font-bold text-amber-300 mt-1">{r.cost} 🪙</p>
                      <p className="text-[10px] font-mono text-slate-500">Estoque: {r.stock}</p>
                      <button
                        onClick={() => deleteReward(r)}
                        data-testid={`delete-reward-${r.id}`}
                        className="w-full mt-2 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-arcade font-semibold flex items-center justify-center gap-1"
                      >
                        <Trash2 size={12} /> Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <Package size={11} /> Resgates ({redemptions.length})
              </p>
              <div className="space-y-2" data-testid="admin-redemptions-list">
                {redemptions.length === 0 && (
                  <p className="text-center text-slate-500 py-4 text-sm">Nenhum resgate ainda</p>
                )}
                {redemptions.map((r) => (
                  <div key={r.id} className="neon-card p-3 flex items-center gap-3" data-testid={`admin-redemption-${r.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                      {r.reward_image ? (
                        <img src={r.reward_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Gift size={16} className="text-fuchsia-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-arcade text-sm font-bold text-white truncate">{r.reward_name}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {r.user_name} • {new Date(r.created_at).toLocaleDateString("pt-BR")}
                      </p>
                      <p className="text-[10px] font-mono text-amber-300">-{r.cost} 🪙</p>
                    </div>
                    {r.status === "delivered" ? (
                      <span className="chip chip-green"><CheckCircle2 size={10} /> Entregue</span>
                    ) : (
                      <button
                        onClick={() => markDelivered(r)}
                        data-testid={`deliver-redemption-${r.id}`}
                        className="chip chip-cyan hover:brightness-110"
                      >
                        <Clock size={10} /> Marcar entregue
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "missions" && (
          <div>
            <button
              onClick={() => setShowNew(true)}
              data-testid="admin-create-quest-btn"
              className="btn-cyan w-full mb-4 flex items-center justify-center gap-2"
            >
              <Plus size={18} /> Nova missão
            </button>
            <div className="space-y-2" data-testid="admin-missions-list">
              {missions.map((m) => (
                <div key={m.id} className="neon-card p-4 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-arcade text-sm font-bold text-white">{m.title}</p>
                      <span className={`chip ${m.scope === "daily" ? "chip-pink" : "chip-cyan"}`}>
                        {m.scope === "daily" ? "Diária" : "Semanal"}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400">{m.description}</p>
                    <p className="text-[10px] font-mono text-amber-300 mt-1">
                      Meta: {m.target_type === "sales_value" ? formatBRL(m.target) : m.target} • +{m.xp_reward} XP
                    </p>
                  </div>
                  <button
                    onClick={() => deleteMission(m.id)}
                    data-testid="admin-delete-mission-btn"
                    className="w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-300 hover:bg-red-500/30"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div>
            <button
              onClick={() => setShowNewUser(true)}
              data-testid="admin-create-user-btn"
              className="btn-cyan w-full mb-3 flex items-center justify-center gap-2"
            >
              <UserPlus size={18} /> Novo usuário
            </button>
            <div className="grid grid-cols-4 gap-1 bg-slate-900/60 p-1 rounded-xl border border-slate-800 mb-4" data-testid="users-filter">
              {[
                { k: "all", label: "Todos" },
                { k: "seller", label: "🎮 Vendedores" },
                { k: "observer", label: "👁️ Observadores" },
                { k: "admin", label: "👑 Admins" },
              ].map((f) => (
                <button
                  key={f.k}
                  data-testid={`users-filter-${f.k}`}
                  onClick={() => setUsersFilter(f.k)}
                  className={`py-2 px-1 rounded-lg text-[10px] font-arcade font-semibold transition-colors ${
                    usersFilter === f.k ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white" : "text-slate-400"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="space-y-2" data-testid="admin-users-list">
              {users.filter((u) => usersFilter === "all" || u.role === usersFilter).map((u) => {
                const isSelf = u.id === me?.id;
                const isMainAdmin = u.email?.toLowerCase() === "al.david8787@gmail.com";
                const protectedUser = isSelf || isMainAdmin;
                return (
                  <div key={u.id} className="neon-card p-4" data-testid={`admin-user-${u.id}`}>
                    <div className="flex items-center gap-3">
                      <Avatar user={u} size={44} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-arcade text-sm font-bold text-white truncate">{u.name}</p>
                          <span className={`chip ${u.role === "admin" ? "chip-gold" : u.role === "observer" ? "chip-cyan" : "chip"}`}>
                            {u.role === "admin" ? "👑 Admin" : u.role === "observer" ? "👁️ Observador" : "🎮 Vendedor"}
                          </span>
                          {isMainAdmin && <span className="chip chip-pink">🔒 Principal</span>}
                        </div>
                        <p className="text-[11px] text-slate-400 truncate">{u.email}</p>
                        <p className="text-[11px] font-mono text-amber-300 mt-0.5">{u.xp || 0} XP</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <select
                        value={u.role}
                        disabled={protectedUser}
                        onChange={(e) => setRole(u, e.target.value)}
                        data-testid={`admin-role-select-${u.id}`}
                        className="flex-1 py-2 rounded-lg text-[11px] font-arcade font-semibold bg-slate-900/60 border border-slate-700 text-slate-200 disabled:opacity-40"
                      >
                        <option value="seller">🎮 Vendedor</option>
                        <option value="observer">👁️ Observador</option>
                        <option value="admin">👑 Admin</option>
                      </select>
                      <button
                        onClick={() => { setShowResetPw(u); setNewPw(""); }}
                        data-testid={`admin-reset-pw-${u.id}`}
                        className="flex-1 py-2 rounded-lg text-[11px] font-arcade font-semibold border border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300 flex items-center justify-center gap-1.5"
                      >
                        <KeyRound size={13} /> Senha
                      </button>
                      <button
                        onClick={() => resetUserXp(u)}
                        disabled={u.role !== "seller"}
                        data-testid={`admin-reset-xp-${u.id}`}
                        className="flex-1 py-2 rounded-lg text-[11px] font-arcade font-semibold border border-slate-700 text-slate-300 hover:border-amber-500/50 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                      >
                        <RotateCcw size={13} /> Zerar XP
                      </button>
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={protectedUser}
                        data-testid={`admin-delete-user-${u.id}`}
                        className="w-10 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showNew && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowNew(false)}>
          <div className="phone-frame w-full" onClick={(e) => e.stopPropagation()}>
            <div className="neon-card-violet mx-3 mb-3 p-6 slide-up" data-testid="new-mission-sheet">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-arcade text-2xl font-bold text-white">Nova Missão</h2>
                <button onClick={() => setShowNew(false)} className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-300">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={createMission} className="space-y-3">
                <input
                  data-testid="mission-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Título"
                  className="arcade-input"
                  required
                />
                <input
                  data-testid="mission-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descrição"
                  className="arcade-input"
                  required
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    data-testid="mission-scope-select"
                    value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                    className="arcade-input"
                  >
                    <option value="daily">Diária</option>
                    <option value="weekly">Semanal</option>
                  </select>
                  <select
                    data-testid="mission-target-type-select"
                    value={form.target_type}
                    onChange={(e) => setForm({ ...form, target_type: e.target.value })}
                    className="arcade-input"
                  >
                    <option value="sales_count">Qtd vendas</option>
                    <option value="sales_value">Valor R$</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    data-testid="mission-target-input"
                    type="number"
                    step="0.01"
                    value={form.target}
                    onChange={(e) => setForm({ ...form, target: e.target.value })}
                    placeholder="Meta"
                    className="arcade-input font-mono"
                    required
                  />
                  <input
                    data-testid="mission-xp-input"
                    type="number"
                    value={form.xp_reward}
                    onChange={(e) => setForm({ ...form, xp_reward: e.target.value })}
                    placeholder="XP"
                    className="arcade-input font-mono"
                    required
                  />
                </div>
                <button type="submit" data-testid="mission-submit-btn" className="btn-arcade w-full">
                  Criar missão
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      {showNewUser && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowNewUser(false)}>
          <div className="phone-frame w-full" onClick={(e) => e.stopPropagation()}>
            <div className="neon-card-violet mx-3 mb-3 p-6 slide-up" data-testid="new-user-sheet">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="chip chip-pink mb-2">👑 Novo acesso</p>
                  <h2 className="font-arcade text-2xl font-bold text-white">Criar Usuário</h2>
                </div>
                <button onClick={() => setShowNewUser(false)} className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-300">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={createUser} className="space-y-3">
                <input
                  data-testid="new-user-name-input"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder="Nome completo"
                  className="arcade-input"
                  required
                />
                <input
                  data-testid="new-user-email-input"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="arcade-input"
                  required
                />
                <input
                  data-testid="new-user-password-input"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Senha (mín. 6 caracteres)"
                  className="arcade-input"
                  minLength={6}
                  required
                />
                <div>
                  <label className="text-xs font-mono text-slate-400 uppercase tracking-widest mb-2 block">Tipo de acesso</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      data-testid="new-user-role-seller"
                      onClick={() => setUserForm({ ...userForm, role: "seller" })}
                      className={`py-3 rounded-xl border font-arcade text-xs font-semibold flex items-center justify-center ${
                        userForm.role === "seller"
                          ? "bg-gradient-to-br from-cyan-500 to-blue-500 text-white border-white/30 shadow-lg"
                          : "bg-slate-900/60 border-slate-700 text-slate-400"
                      }`}
                    >
                      🎮 Vendedor
                    </button>
                    <button
                      type="button"
                      data-testid="new-user-role-observer"
                      onClick={() => setUserForm({ ...userForm, role: "observer" })}
                      className={`py-3 rounded-xl border font-arcade text-xs font-semibold flex items-center justify-center ${
                        userForm.role === "observer"
                          ? "bg-gradient-to-br from-indigo-500 to-cyan-500 text-white border-white/30 shadow-lg"
                          : "bg-slate-900/60 border-slate-700 text-slate-400"
                      }`}
                    >
                      👁️ Observador
                    </button>
                    <button
                      type="button"
                      data-testid="new-user-role-admin"
                      onClick={() => setUserForm({ ...userForm, role: "admin" })}
                      className={`py-3 rounded-xl border font-arcade text-xs font-semibold flex items-center justify-center ${
                        userForm.role === "admin"
                          ? "bg-gradient-to-br from-amber-400 to-pink-500 text-white border-white/30 shadow-lg"
                          : "bg-slate-900/60 border-slate-700 text-slate-400"
                      }`}
                    >
                      👑 Admin
                    </button>
                  </div>
                  {userForm.role === "admin" && (
                    <p className="text-[11px] text-amber-300 mt-2 flex items-center gap-1.5">
                      <AlertTriangle size={12} /> Admins têm acesso total — crie com cautela.
                    </p>
                  )}
                  {userForm.role === "observer" && (
                    <p className="text-[11px] text-cyan-300 mt-2">
                      👁️ Observador só visualiza — não vende, não resgata, não aparece no ranking.
                    </p>
                  )}
                </div>
                <button type="submit" data-testid="new-user-submit-btn" className="btn-arcade w-full">
                  Criar usuário
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showResetPw && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowResetPw(null)}>
          <div className="phone-frame w-full" onClick={(e) => e.stopPropagation()}>
            <div className="neon-card-violet mx-3 mb-3 p-6 slide-up" data-testid="reset-pw-sheet">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="chip chip-cyan mb-2">🔑 Redefinir senha</p>
                  <h2 className="font-arcade text-xl font-bold text-white">{showResetPw.name}</h2>
                  <p className="text-[11px] text-slate-400">{showResetPw.email}</p>
                </div>
                <button onClick={() => setShowResetPw(null)} className="w-10 h-10 rounded-full bg-slate-800/80 flex items-center justify-center text-slate-300">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={resetPassword} className="space-y-3">
                <input
                  data-testid="reset-pw-input"
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  placeholder="Nova senha (mín. 6 caracteres)"
                  className="arcade-input"
                  minLength={6}
                  required
                  autoFocus
                />
                <button type="submit" data-testid="reset-pw-submit-btn" className="btn-arcade w-full">
                  Salvar nova senha
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
